import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Search, RefreshCcw, Download, Mail, CheckCircle2, AlertCircle,
  Wallet, Calendar, Ban, ShieldCheck, MinusCircle, FileText, Banknote,
  PlayCircle, Building2, Settings2, BellRing, XCircle, StickyNote, HelpCircle,
  ChevronDown, ChevronUp, FilePlus2, Info, Wrench, Zap, Undo2, Clock,
  CreditCard,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const fmtEur = (v) =>
  v == null ? "—" : new Intl.NumberFormat("de-DE", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2,
  }).format(Number(v));

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

const STATUS_META = {
  draft: { label: "Entwurf", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  sent: { label: "Verschickt", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  paid: { label: "Bezahlt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Fehlgeschlagen", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { label: "Storniert", cls: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through" },
  refunded: { label: "Erstattet", cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

/**
 * Master list of all row-level actions — used by:
 *  • the Help-Legend (`ActionsLegend`) so the admin sees what each button does
 *  • the action cell in the table (icon + label + tooltip)
 *
 * `shouldShow` is evaluated per row to decide if the button renders.
 */
const ACTION_DEFINITIONS = [
  {
    key: "generate",
    label: "Rechnung erzeugen",
    icon: FilePlus2,
    color: "sky",
    description: "Erzeugt/aktualisiert den Rechnungs-Entwurf manuell (Grundgebühr + Akten × Akten-Preis + 19 % USt). Entwürfe werden ansonsten automatisch täglich vom System erstellt.",
    shouldShow: (r) => !r.invoice,
  },
  {
    key: "pdf",
    label: "PDF",
    icon: Download,
    color: "zinc",
    description: "Lädt die Rechnung als PDF herunter. Wird bei jedem Klick neu generiert.",
    shouldShow: (r) => !!r.invoice,
  },
  {
    key: "send",
    label: "Senden",
    icon: Mail,
    color: "sky",
    description: "Versendet die Rechnung als E-Mail (mit PDF-Anhang) UND zieht den Betrag SOFORT per Stripe-SEPA-Lastschrift ein. Ohne Stripe-fähiges SEPA-Mandat wird der Versand blockiert. Der Einzugs-Status erscheint direkt an der Rechnung.",
    shouldShow: (r) => !!r.invoice && r.invoice.status !== "paid" && r.invoice.status !== "cancelled",
  },
  {
    key: "paid",
    label: "Bezahlt",
    icon: CheckCircle2,
    color: "emerald",
    description: "Markiert die Rechnung manuell als bezahlt (z.B. nach SEPA-Eingang). Setzt das Bezahl-Datum auf jetzt.",
    shouldShow: (r) => !!r.invoice && r.invoice.status !== "paid" && r.invoice.status !== "cancelled",
  },
  {
    key: "collect",
    label: "Stripe-Einzug",
    icon: Zap,
    color: "emerald",
    description: "Zieht den Rechnungsbetrag SOFORT per Stripe-SEPA-Lastschrift vom Kunden ein (nutzt das beim Abo hinterlegte Mandat). Der Status aktualisiert sich automatisch: SEPA braucht 2–5 Banktage. Bei Fehlschlag erscheint der Grund an der Rechnung.",
    shouldShow: (r) => !!r.invoice
      && ["sent", "failed"].includes(r.invoice.status)
      && !["processing", "succeeded"].includes(r.invoice.collection_status || ""),
  },
  {
    key: "refund",
    label: "Rückerstattung",
    icon: Undo2,
    color: "violet",
    description: "Erstattet einen per Stripe eingezogenen Betrag ganz oder teilweise an den Kunden zurück (SEPA-Gutschrift, dauert einige Banktage).",
    shouldShow: (r) => !!r.invoice
      && ["paid", "refunded"].includes(r.invoice.status)
      && !!r.invoice.stripe_payment_intent_id,
  },
  {
    key: "reminder",
    label: "Erinnerung",
    icon: BellRing,
    color: "amber",
    description: "Sendet eine Zahlungserinnerung oder Mahnung. 3 Stufen: 1 = freundliche Erinnerung, 2 = 1. Mahnung, 3 = letzte Mahnung. Trackt die Mahnstufe an der Rechnung.",
    shouldShow: (r) => !!r.invoice && r.invoice.status !== "paid" && r.invoice.status !== "cancelled",
  },
  {
    key: "deduct",
    label: "Akte übernehmen",
    icon: MinusCircle,
    color: "amber",
    description: "Übernimmt eine konkrete Akte des Gutachters als Kompensation für die offene Abo-Rechnung. EasyGutachter rechnet diese Akte direkt mit der Versicherung ab. Das Honorar wird zusätzlich als Gutschrift auf die offene Abo-Rechnung des Gutachters gebucht. Vorschlag: teuerste verfügbare Akte des Monats.",
    shouldShow: () => true,
  },
  {
    key: "note",
    label: "Notiz",
    icon: StickyNote,
    color: "violet",
    description: "Interne Admin-Notiz an der Rechnung (z.B. 'Rückläufer 12.02., Kunde kontaktiert'). Wird NICHT mit dem Kunden geteilt — nur im Cockpit sichtbar.",
    shouldShow: (r) => !!r.invoice,
  },
  {
    key: "cancel",
    label: "Stornieren",
    icon: XCircle,
    color: "rose",
    description: "Storniert die Rechnung (Status → 'Storniert'). Erfordert einen Stornogrund. Bezahlte Rechnungen können NICHT storniert werden (nutze stattdessen die Gutschrift).",
    shouldShow: (r) => !!r.invoice && r.invoice.status !== "paid" && r.invoice.status !== "cancelled",
  },
  {
    key: "block",
    label: "Sperren",
    icon: Ban,
    color: "rose",
    description: "Sperrt den Gutachter komplett: Login wird sofort verhindert. Audit-Log wird geschrieben. Sich selbst sperren ist nicht möglich.",
    shouldShow: (r) => !r.is_blocked,
  },
  {
    key: "unblock",
    label: "Entsperren",
    icon: ShieldCheck,
    color: "emerald",
    description: "Hebt die Sperre eines Gutachters wieder auf. Login ist sofort wieder möglich.",
    shouldShow: (r) => r.is_blocked,
  },
];

const COLOR_BTN = {
  sky:     "text-sky-700 hover:bg-sky-50 border-sky-200",
  emerald: "text-emerald-700 hover:bg-emerald-50 border-emerald-200",
  amber:   "text-amber-700 hover:bg-amber-50 border-amber-200",
  violet:  "text-violet-700 hover:bg-violet-50 border-violet-200",
  rose:    "text-rose-700 hover:bg-rose-50 border-rose-200",
  zinc:    "text-zinc-700 hover:bg-zinc-50 border-zinc-200",
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "bg-zinc-100 text-zinc-700" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${meta.cls}`} data-testid={`status-pill-${status}`}>{meta.label}</span>;
}

/* Einzugs-Status (Stripe): processing → ⏳, succeeded → ✅, failed → ❌ */
function CollectionBadge({ inv, onSync, syncing }) {
  if (!inv?.collection_status) return null;
  if (inv.collection_status === "processing") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 mt-1" data-testid="collection-badge-processing" title={`Einzug gestartet ${fmtDate(inv.collection_started_at)} — SEPA braucht 2–5 Banktage`}>
        <Clock className="h-3 w-3" /> Einzug läuft (Stripe)
        {onSync && (
          <button
            type="button"
            onClick={onSync}
            disabled={syncing}
            className="ml-0.5 p-0.5 rounded text-sky-600 hover:text-sky-900 hover:bg-sky-100 disabled:opacity-50 transition-colors"
            title="Jetzt live bei Stripe prüfen, ob abgebucht wurde"
            data-testid={`collect-sync-btn-${inv.id}`}
          >
            <RefreshCcw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
          </button>
        )}
      </span>
    );
  }
  if (inv.collection_status === "succeeded" || inv.paid_via === "stripe") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 mt-1" data-testid="collection-badge-succeeded" title={inv.paid_at ? `Stripe hat abgebucht — bezahlt am ${fmtDate(inv.paid_at)}` : "Stripe hat abgebucht"}>
        <Zap className="h-3 w-3" /> Per Stripe eingezogen
      </span>
    );
  }
  if (inv.collection_status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 mt-1" data-testid="collection-badge-failed" title={inv.collection_error || "SEPA-Lastschrift fehlgeschlagen"}>
        <AlertCircle className="h-3 w-3" /> Einzug fehlgeschlagen
      </span>
    );
  }
  return null;
}

function KpiCard({ icon: Icon, label, value, accent = "sky", testid }) {
  const cmap = {
    sky: "from-sky-50 via-white to-sky-50 border-sky-200 text-sky-800",
    violet: "from-violet-50 via-white to-violet-50 border-violet-200 text-violet-800",
    zinc: "from-zinc-50 via-white to-zinc-50 border-zinc-200 text-zinc-800",
    emerald: "from-emerald-50 via-white to-emerald-50 border-emerald-200 text-emerald-800",
    rose: "from-rose-50 via-white to-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`rounded-2xl border-2 bg-gradient-to-br ${cmap[accent]} p-3.5 flex items-center gap-3`} data-testid={testid}>
      <div className="h-9 w-9 rounded-xl bg-white/80 border border-current/20 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
        <div className="text-xl font-bold mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function AdminBilling() {
  return <Layout><AdminBillingContent /></Layout>;
}

export function AdminBillingContent() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [running, setRunning] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [syncingCollect, setSyncingCollect] = useState(false);

  // Dialogs
  const [blockDlg, setBlockDlg] = useState(null);   // { owner }
  const [deductDlg, setDeductDlg] = useState(null); // { owner, cases_count }
  const [reminderDlg, setReminderDlg] = useState(null); // { row }
  const [cancelDlg, setCancelDlg] = useState(null); // { row }
  const [noteDlg, setNoteDlg] = useState(null); // { row }
  const [sepaDlg, setSepaDlg] = useState(false);
  // Lokal gemerkte, gerade erfolgreich übertragene Mandate — zeigt sofort
  // "Bereits an Stripe übertragen", auch bevor der Reload durch ist.
  const [pushedIds, setPushedIds] = useState(() => new Set());
  const [creditorDlg, setCreditorDlg] = useState(false);
  const [stripeDlg, setStripeDlg] = useState(false);
  const [refundDlg, setRefundDlg] = useState(null); // { row }
  const [helpOpen, setHelpOpen] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/admin/billing/unified?year=${year}&month=${month}`);
      setData(r);
    } catch (e) {
      toast.error(apiError(e, "Daten konnten nicht geladen werden"));
    } finally { setLoading(false); }
  };

  const onBackfillReleasedAt = async () => {
    if (!window.confirm(
      "Counter reparieren: Setzt einen Freigabe-Zeitstempel für Bestandsakten, "
      + "die bereits abgeschlossen/versendet sind aber noch nicht im Counter erscheinen.\n\n"
      + "Diese Aktion ist sicher (idempotent) und ändert nur Akten, die noch keinen "
      + "Freigabe-Zeitstempel haben.\n\nFortfahren?"
    )) return;
    setBackfillLoading(true);
    try {
      const { data: r } = await api.post(`/admin/billing/backfill-released-at`);
      if (r.updated > 0) {
        toast.success(`✅ ${r.message || `${r.updated} Akten nachträglich freigegeben`}`, {
          description: "Counter aktualisiert sich gleich automatisch.",
          duration: 10000,
        });
      } else {
        toast.info(r.message || "Nichts zu tun — alle Akten haben bereits einen Freigabe-Zeitstempel.",
          { duration: 10000 });
      }
      await load();
    } catch (e) {
      toast.error(apiError(e, "Counter konnte nicht repariert werden"));
    } finally {
      setBackfillLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const n = q.trim().toLowerCase();
    let f = all;
    if (filter === "with_sepa") f = f.filter((r) => r.sepa?.active);
    else if (filter === "without_sepa") f = f.filter((r) => !r.sepa?.active);
    else if (filter === "blocked") f = f.filter((r) => r.is_blocked);
    else if (filter === "unpaid") f = f.filter((r) => (r.invoice?.status || "draft") !== "paid");
    else if (filter === "collected") f = f.filter((r) => r.invoice && (r.invoice.collection_status === "succeeded" || r.invoice.paid_via === "stripe"));
    else if (filter === "collecting") f = f.filter((r) => r.invoice?.collection_status === "processing");
    else if (filter === "collect_failed") f = f.filter((r) => r.invoice?.collection_status === "failed");
    if (n) f = f.filter((r) =>
      [r.owner_name, r.owner_email, r.company, r.invoice?.invoice_number]
        .filter(Boolean).some((v) => v.toLowerCase().includes(n))
    );
    return f;
  }, [data, q, filter]);

  const k = data?.kpis || {};

  // Aktions-Handler pro Zeile (Desktop-Tabelle + Mobile-Cards)
  const handlersFor = (r) => {
    const inv = r.invoice;
    return {
      generate: () => generateOne(r.owner_id),
      pdf: () => inv && downloadPdf(inv.id, inv.invoice_number),
      send: () => inv && sendInvoice(inv.id),
      paid: () => inv && markPaid(inv.id),
      collect: () => inv && collectOne(r),
      refund: () => setRefundDlg({ row: r }),
      reminder: () => setReminderDlg({ row: r }),
      deduct: () => setDeductDlg({ owner: r }),
      note: () => setNoteDlg({ row: r }),
      cancel: () => setCancelDlg({ row: r }),
      block: () => setBlockDlg({ owner: r }),
      unblock: () => unblock(r.owner_id),
    };
  };

  // Aktionen
  const runPeriod = async () => {
    if (!window.confirm(`Entwürfe für ${MONTHS[month - 1]} ${year} jetzt neu berechnen?\n\nBereits versendete/bezahlte Rechnungen bleiben unangetastet.`)) return;
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/subscription-invoices/run-period", { year, month, force: false });
      toast.success(`${r.count} Entwürfe aktualisiert`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Lauf fehlgeschlagen"));
    } finally { setRunning(false); }
  };

  const sendAll = async () => {
    const n = k.draft_count ?? 0;
    if (!window.confirm(
      `${n} kontrollierte ${n === 1 ? "Entwurf" : "Entwürfe"} für ${MONTHS[month - 1]} ${year} jetzt versenden?\n\n`
      + "Jede Rechnung wird per E-Mail verschickt und der Betrag SOFORT per Stripe-SEPA eingezogen.\n"
      + "Kunden ohne Stripe-fähiges SEPA-Mandat werden übersprungen (kein Versand)."
    )) return;
    setSendingAll(true);
    try {
      const { data: r } = await api.post("/admin/subscription-invoices/send-all", { year, month });
      const parts = [`${r.sent_count} versendet`, `${r.collected_count ?? 0} Einzug gestartet`];
      if (r.collect_failed_count) parts.push(`${r.collect_failed_count} Einzug fehlgeschlagen`);
      if (r.skipped_no_mandate_count) parts.push(`${r.skipped_no_mandate_count} ohne Mandat übersprungen`);
      if (r.failed_count) parts.push(`${r.failed_count} Fehler`);
      toast.success(parts.join(" · "), { duration: 10000 });
      if (r.skipped_no_mandate_count) {
        toast.warning(
          `Ohne SEPA-Mandat blockiert: ${(r.skipped_no_mandate || []).map((s) => s.owner_email).join(", ")}`.slice(0, 400),
          { duration: 12000 },
        );
      }
      await load();
    } catch (e) {
      toast.error(apiError(e, "Sammel-Versand fehlgeschlagen"));
    } finally { setSendingAll(false); }
  };

  const generateOne = async (ownerId) => {
    setBusyId(`gen-${ownerId}`);
    try {
      await api.post("/admin/subscription-invoices/generate", { owner_id: ownerId, year, month, force: true });
      toast.success("Rechnung erzeugt");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Erzeugen fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const markPaid = async (invId) => {
    if (!window.confirm("Rechnung als bezahlt markieren?")) return;
    setBusyId(`paid-${invId}`);
    try {
      await api.post(`/admin/subscription-invoices/${invId}/mark-paid`);
      toast.success("Als bezahlt markiert");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Aktion fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const collectOne = async (r) => {
    const inv = r.invoice;
    if (!r.stripe?.ready) {
      toast.error("Kein Stripe-SEPA-Mandat hinterlegt", {
        description: r.sepa?.active
          ? `${r.owner_name} hat ein App-Mandat — übertrage es mit „→ Stripe übertragen" in der SEPA-Spalte.`
          : `${r.owner_name} muss das SEPA-Mandat im Abo-Bereich einrichten — oder nutze den SEPA-XML-Export.`,
      });
      return;
    }
    if (!window.confirm(
      `${fmtEur(inv.total_gross)} jetzt per Stripe-SEPA von ${r.owner_name} einziehen?\n\n`
      + `Rechnung ${inv.invoice_number}\nDer Einzug dauert 2–5 Banktage; der Status aktualisiert sich automatisch.`
    )) return;
    setBusyId(`collect-${inv.id}`);
    try {
      await api.post(`/admin/subscription-invoices/${inv.id}/collect`);
      toast.success("Einzug gestartet", { description: "Stripe zieht den Betrag per SEPA ein — Status folgt automatisch." });
      await load();
    } catch (e) {
      toast.error(apiError(e, "Einzug fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const pushMandate = async (r) => {
    if (!window.confirm(
      `SEPA-Mandat von ${r.owner_name} jetzt zu Stripe übertragen?\n\n`
      + `IBAN ${r.sepa?.iban_masked || ""} · Mandat ${r.sepa?.mandate_reference || ""}\n`
      + "Es wird KEIN Geld abgebucht — nur die Zahlungsmethode wird in Stripe angelegt."
    )) return;
    setBusyId(`push-${r.owner_id}`);
    try {
      // Hintergrund-Job + Polling — verhindert Proxy-Timeouts (Cloudflare) bei
      // langsamen Stripe-Antworten in Produktion.
      await api.post(`/admin/subscription-invoices/collect/push-mandate/${r.owner_id}`);
      const deadline = Date.now() + 120000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        const { data: job } = await api.get(`/admin/subscription-invoices/collect/push-mandate/${r.owner_id}/status`);
        if (job.status === "error") throw new Error(job.error || "Übertragung fehlgeschlagen");
        if (job.status === "done") {
          setPushedIds((prev) => { const n = new Set(prev); n.add(r.owner_id); return n; });
          toast.success("SEPA-Mandat zu Stripe übertragen", {
            description: `${r.owner_name} kann jetzt per Stripe-Einzug abgebucht werden.`,
          });
          await load();
          return;
        }
      }
      throw new Error("Zeitüberschreitung — bitte Seite aktualisieren und Status prüfen");
    } catch (e) {
      toast.error(e?.response ? apiError(e, "Übertragung fehlgeschlagen") : (e?.message || "Übertragung fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const syncAllCollections = async () => {
    setSyncingCollect(true);
    try {
      const { data: r } = await api.post("/admin/subscription-invoices/collect/sync-status");
      if (!r.checked) {
        toast.info("Keine laufenden Einzüge zu prüfen");
      } else {
        const parts = [`${r.checked} geprüft`];
        if (r.paid) parts.push(`${r.paid} abgebucht ✓`);
        if (r.failed) parts.push(`${r.failed} fehlgeschlagen`);
        if (r.still_processing) parts.push(`${r.still_processing} laufen noch`);
        if (r.errors) parts.push(`${r.errors} Abfrage-Fehler`);
        toast.success(`Stripe-Abgleich: ${parts.join(" · ")}`, { duration: 9000 });
      }
      await load();
    } catch (e) {
      toast.error(apiError(e, "Stripe-Abgleich fehlgeschlagen"));
    } finally { setSyncingCollect(false); }
  };

  const syncCollectOne = async (invId) => {
    setBusyId(`csync-${invId}`);
    try {
      const { data: r } = await api.post(`/admin/subscription-invoices/${invId}/collect/sync`);
      const st = r?.sync?.result;
      if (st === "paid") toast.success("Stripe hat abgebucht ✓ — Rechnung ist bezahlt");
      else if (st === "failed") toast.warning(`Einzug fehlgeschlagen: ${r?.sync?.error || "Grund siehe Rechnung"}`, { duration: 9000 });
      else toast.info("Einzug läuft noch — SEPA braucht 2–5 Banktage");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Stripe-Abfrage fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const toggleBillingMode = async (r) => {    const toStripe = !(r.billing_mode === "stripe_auto" || r.stripe?.subscription);
    const msg = toStripe
      ? `${r.owner_name} auf Stripe-Voll-Automatik umstellen?\n\nStripe legt ein echtes Abo (Grundgebühr + Akten-Zähler) an und bucht die Monatsrechnungen selbstständig ab. Interner Versand & Einzug werden für diesen Kunden gesperrt.`
      : `${r.owner_name} auf Software-Einzug (Standard) umstellen?\n\nDas Stripe-Abo wird SOFORT beendet — ab jetzt erstellt die Software die Rechnungen und zieht beim Senden per SEPA ein.\n\nHinweis: Bereits von Stripe abgerechnete Akten des laufenden Monats ggf. per „Akte übernehmen/abziehen" korrigieren (Doppel-Abrechnung vermeiden).`;
    if (!window.confirm(msg)) return;
    setBusyId(`mode-${r.owner_id}`);
    try {
      await api.post(`/admin/subscription-invoices/billing-mode/${r.owner_id}`, { mode: toStripe ? "stripe_auto" : "software" });
      toast.success(toStripe ? "Stripe-Voll-Automatik aktiviert" : "Auf Software-Einzug umgestellt");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Umstellung fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const sendInvoice = async (invId) => {
    if (!window.confirm(
      "Rechnung jetzt senden?\n\nDie Rechnung wird per E-Mail verschickt und der Betrag SOFORT per Stripe-SEPA eingezogen (2–5 Banktage)."
    )) return;
    setBusyId(`send-${invId}`);
    try {
      const { data: r } = await api.post(`/admin/subscription-invoices/${invId}/send`);
      if (r?.collect?.error) {
        toast.warning("Rechnung versendet — Stripe-Einzug fehlgeschlagen", {
          description: r.collect.error, duration: 10000,
        });
      } else {
        toast.success("Rechnung versendet · Stripe-Einzug gestartet", {
          description: "SEPA braucht 2–5 Banktage — der Status aktualisiert sich automatisch.",
        });
      }
      await load();
    } catch (e) {
      toast.error(apiError(e, "Versand fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const downloadPdf = async (invId, num) => {
    setBusyId(`pdf-${invId}`);
    try {
      const r = await api.get(`/admin/subscription-invoices/${invId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Rechnung-${num || invId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "Download fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const unblock = async (ownerId) => {
    if (!window.confirm("User wirklich entsperren?")) return;
    setBusyId(`unblock-${ownerId}`);
    try {
      await api.post(`/admin/billing/${ownerId}/unblock`);
      toast.success("User entsperrt");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Entsperren fehlgeschlagen"));
    } finally { setBusyId(null); }
  };

  const yearOptions = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  return (
    <>
      <div className="space-y-6" data-testid="admin-billing-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-sky-700">Buchhaltung</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
              Buchhaltung · SEPA-Einzug
            </h1>
            <p className="text-sm text-zinc-600 mt-1.5 max-w-2xl">
              Rechnungs-Entwürfe werden <b>automatisch pro Benutzer erstellt</b> und täglich aktualisiert.
              Die Buchhaltung <b>kontrolliert und versendet</b> — beim Versand wird der Betrag
              <b> sofort per Stripe-SEPA eingezogen</b> und der Einzugs-Status hier live angezeigt.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="h-10 rounded-lg border border-zinc-300 px-3 text-sm bg-white"
              data-testid="billing-year-select"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="h-10 rounded-lg border border-zinc-300 px-3 text-sm bg-white"
              data-testid="billing-month-select"
            >
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="billing-refresh-btn">
              <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Neu laden
            </Button>
            <Button
              onClick={syncAllCollections}
              variant="outline" size="sm"
              disabled={syncingCollect}
              className="border-sky-300 text-sky-800 hover:bg-sky-50"
              data-testid="billing-collect-sync-btn"
              title="Fragt für alle laufenden SEPA-Einzüge den Status live bei Stripe ab: abgebucht, fehlgeschlagen oder läuft noch. Läuft zusätzlich automatisch jede Stunde."
            >
              {syncingCollect ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
              Abbuchungen prüfen
            </Button>
            <Button onClick={() => setCreditorDlg(true)} variant="outline" size="sm" data-testid="billing-creditor-btn">
              <Settings2 className="h-4 w-4 mr-1.5" /> Gläubiger
            </Button>
            <Button onClick={() => setHelpOpen((v) => !v)} variant="outline" size="sm" data-testid="billing-help-btn">
              <HelpCircle className="h-4 w-4 mr-1.5" /> {helpOpen ? "Hilfe ausblenden" : "Aktionen erklärt"}
              {helpOpen ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
            </Button>
            <Button
              onClick={onBackfillReleasedAt}
              variant="outline" size="sm"
              disabled={backfillLoading}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="billing-backfill-btn"
              title="Setzt Freigabe-Zeitstempel für Bestandsakten, die schon abgeschlossen/versendet sind aber noch keinen Counter-Eintrag haben. Idempotent — mehrfach klicken ist sicher."
            >
              {backfillLoading
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Wrench className="h-4 w-4 mr-1.5" />}
              Counter reparieren
            </Button>
          </div>
        </div>

        {/* Help Legend (collapsible) */}
        {helpOpen && <ActionsLegend onClose={() => setHelpOpen(false)} />}

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="billing-kpis">
          <KpiCard icon={Wallet} label="Brutto offen" value={fmtEur(k.total_gross_invoiced)} accent="sky" testid="kpi-gross" />
          <KpiCard icon={FileText} label="Entwürfe" value={k.draft_count ?? 0} accent="zinc" testid="kpi-draft" />
          <KpiCard icon={CheckCircle2} label="Bezahlt" value={k.paid_count ?? 0} accent="emerald" testid="kpi-paid" />
          <KpiCard icon={Ban} label="Gesperrt" value={k.blocked_count ?? 0} accent={k.blocked_count ? "rose" : "zinc"} testid="kpi-blocked" />
          <KpiCard icon={Zap} label="Eingezogen (Stripe)" value={k.collected_count ?? 0} accent="emerald" testid="kpi-collected" />
          <KpiCard icon={Clock} label="Einzug läuft" value={k.collecting_count ?? 0} accent="sky" testid="kpi-collecting" />
          <KpiCard icon={AlertCircle} label="Einzug fehlgeschlagen" value={k.collect_failed_count ?? 0} accent={k.collect_failed_count ? "rose" : "zinc"} testid="kpi-collect-failed" />
          <KpiCard icon={CreditCard} label="Stripe-Automatik" value={k.stripe_auto_count ?? 0} accent="violet" testid="kpi-stripe-auto" />
        </div>

        {/* Abrechnungs-Modus Banner */}
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-start gap-3 flex-wrap" data-testid="billing-mode-banner">
          <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <Zap className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm font-bold text-emerald-900">Software-geführte Abrechnung (Standard)</div>
            <div className="text-xs text-emerald-800/80 mt-0.5">
              Die Software erstellt die Rechnungen. <b>„Senden" verschickt die Rechnung per E-Mail und
              zieht den Betrag sofort per Stripe-SEPA ein</b> — ob der Einzug geklappt hat, siehst du
              direkt an der Rechnung (Einzug läuft → eingezogen / fehlgeschlagen). Ohne Stripe-fähiges
              SEPA-Mandat wird der Versand blockiert. Einzelne Kunden können in der Tabelle optional auf
              <b> Stripe-Voll-Automatik</b> umgestellt werden (Stripe legt Abo + Akten-Zähler an und bucht selbst ab).
            </div>
          </div>
        </div>

        {/* Action Bar — Automatik & Sammel-Aktionen */}
        <div className="rounded-2xl border-2 border-sky-200 bg-gradient-to-br from-sky-50/60 via-white to-violet-50/30 p-5" data-testid="billing-automation-bar">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <Banknote className="h-4 w-4 text-sky-700" />
                <h2 className="font-display text-lg font-bold tracking-tight text-zinc-900">
                  Automatik · {MONTHS[month - 1]} {year}
                </h2>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-testid="billing-automation-active">
                  <span className="relative inline-flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  Aktiv
                </span>
              </div>
              <p className="text-xs text-zinc-600 mt-1 max-w-xl">
                Entwürfe werden <b>automatisch pro Benutzer erstellt</b> und täglich aktualisiert —
                hier nur noch <b>kontrollieren und versenden</b>.
              </p>
              <p className="text-xs text-zinc-600 mt-1">
                {data ? <>{k.subscribers} aktive Abonnenten · {k.with_sepa} mit SEPA-Mandat · Brutto-Schätzung {fmtEur(k.total_gross_estimate)}</> : "Lädt…"}
              </p>
              {(k.draft_count ?? 0) > 0 && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold" data-testid="billing-drafts-ready-banner">
                  <FileText className="h-3.5 w-3.5" />
                  {k.draft_count} {k.draft_count === 1 ? "Entwurf" : "Entwürfe"} bereit zur Kontrolle
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={runPeriod}
                disabled={running || !(k.subscribers > 0)}
                variant="outline"
                data-testid="billing-run-period-btn"
              >
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
                Entwürfe aktualisieren
              </Button>
              <Button
                onClick={sendAll}
                disabled={sendingAll || !(k.draft_count > 0)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="billing-send-all-btn"
              >
                {sendingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Alle Entwürfe versenden{(k.draft_count ?? 0) > 0 ? ` (${k.draft_count})` : ""}
              </Button>
              <Button
                onClick={() => setSepaDlg(true)}
                disabled={!(k.with_sepa > 0)}
                className="bg-gradient-to-r from-sky-500 to-violet-600 hover:from-sky-600 hover:to-violet-700 text-white"
                data-testid="billing-sepa-xml-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                SEPA-XML herunterladen
              </Button>
              <Button
                onClick={() => setStripeDlg(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="billing-stripe-collect-btn"
              >
                <Zap className="h-4 w-4 mr-2" />
                Stripe-Einzug
              </Button>
            </div>
          </div>
        </div>

        {/* Filter + Search */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              ["all", "Alle"],
              ["unpaid", "Offen"],
              ["collected", "Eingezogen"],
              ["collecting", "Einzug läuft"],
              ["collect_failed", "Einzug fehlgeschlagen"],
              ["with_sepa", "Mit SEPA"],
              ["without_sepa", "Ohne SEPA"],
              ["blocked", "Gesperrt"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={`px-3 h-8 rounded-full text-xs font-semibold transition ${
                  filter === v
                    ? "bg-gradient-to-r from-sky-500 to-violet-600 text-white"
                    : "bg-white border border-zinc-200 text-zinc-700 hover:border-sky-400"
                }`}
                data-testid={`filter-${v}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-auto sm:ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Suche Gutachter, Rechnungs-Nr…"
              className="pl-9 h-9 w-full sm:w-72"
              data-testid="billing-search"
            />
          </div>
        </div>

        {/* Unified Table (Desktop) */}
        <div className="hidden xl:block rounded-2xl border border-zinc-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-unified-table">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Gutachter</th>
                  <th className="text-left px-3 py-2.5">Tarif</th>
                  <th className="text-right px-3 py-2.5">Akten</th>
                  <th className="text-right px-3 py-2.5">Brutto</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">SEPA-Mandat</th>
                  <th className="text-right px-3 py-2.5">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /> Lade…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-zinc-400">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                    <div className="font-medium">Keine Daten</div>
                  </td></tr>
                ) : rows.map((r) => {
                  const inv = r.invoice;
                  const blocked = r.is_blocked;
                  return (
                    <tr key={r.owner_id} className={`hover:bg-zinc-50/60 ${blocked ? "bg-rose-50/30" : ""}`} data-testid={`row-${r.owner_id}`}>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-zinc-900">{r.owner_name}</div>
                          {blocked && <Badge className="bg-rose-100 text-rose-800 text-[10px]" data-testid={`blocked-badge-${r.owner_id}`}><Ban className="h-3 w-3 mr-1" />Gesperrt</Badge>}
                        </div>
                        <div className="text-xs text-zinc-500">{r.owner_email}</div>
                        {r.company && <div className="text-xs text-zinc-400 flex items-center gap-1"><Building2 className="h-3 w-3" />{r.company}</div>}
                        {inv?.invoice_number && (
                          <div className="text-[11px] font-mono text-sky-700 mt-1">{inv.invoice_number}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <Badge variant="outline" className="text-xs">{r.plan_name}</Badge>
                        {r.subscription_status && r.subscription_status !== "active" && (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px] ml-1" data-testid={`sub-status-badge-${r.owner_id}`}>
                            Abo: {r.subscription_status}
                          </Badge>
                        )}
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {fmtEur(r.base_fee)} + {fmtEur(r.per_case_fee)}/Akte
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right align-top">
                        <div className="font-mono text-sm font-semibold">{r.effective_cases}</div>
                        {r.stripe?.subscription && (
                          <div
                            className={`text-[10px] font-semibold ${r.stripe_metered_cases >= r.effective_cases ? "text-sky-700" : "text-amber-700"}`}
                            title="An den Stripe-Zähler gemeldete Akten dieser Periode — Stripe rechnet: Anzahl × Akten-Preis + 19 % USt"
                            data-testid={`stripe-metered-${r.owner_id}`}
                          >
                            Stripe: {r.stripe_metered_cases} gemeldet{r.stripe_metered_cases >= r.effective_cases ? " ✓" : ""}
                          </div>
                        )}
                        {r.cases_taken_over > 0 && (
                          <div className="text-[10px] text-amber-700 font-semibold" data-testid={`takeover-count-${r.owner_id}`}>
                            −{r.cases_taken_over} übernommen ({fmtEur(r.takeover_total)})
                          </div>
                        )}
                        {r.cases_deducted > 0 && (
                          <div className="text-[10px] text-rose-600">−{r.cases_deducted} abgezogen</div>
                        )}
                        {r.cases_count !== r.effective_cases && (
                          <div className="text-[10px] text-zinc-400">(roh: {r.cases_count})</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right align-top">
                        <div className="font-bold text-zinc-900">
                          {inv ? fmtEur(inv.total_gross) : <span className="text-zinc-400">{fmtEur(r.gross_estimate)} <span className="text-[10px]">(geschätzt)</span></span>}
                        </div>
                        {inv?.due_date && (
                          <div className="text-[10px] text-zinc-500 flex items-center justify-end gap-1 mt-0.5">
                            <Calendar className="h-3 w-3" />{fmtDate(inv.due_date)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {inv ? (
                          <div>
                            <StatusPill status={inv.status} />
                            <div><CollectionBadge inv={inv} onSync={() => syncCollectOne(inv.id)} syncing={busyId === `csync-${inv.id}`} /></div>
                            {inv.reminder_level > 0 && (
                              <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`reminder-level-${r.owner_id}`}>
                                <BellRing className="h-3 w-3" />
                                {inv.reminder_level === 1 ? "Erinnert" : `Mahnung ${inv.reminder_level - 1}`}
                              </div>
                            )}
                            {inv.admin_note && (
                              <div className="text-[10px] text-violet-700 mt-1 flex items-start gap-1 max-w-[180px]" data-testid={`note-indicator-${r.owner_id}`} title={inv.admin_note}>
                                <StickyNote className="h-3 w-3 shrink-0 mt-0.5" />
                                <span className="truncate">{inv.admin_note}</span>
                              </div>
                            )}
                          </div>
                        ) : <span className="text-xs text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {r.sepa?.active ? (
                          <div>
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">
                              <ShieldCheck className="h-3 w-3 mr-1" />Aktiv
                            </Badge>
                            <div className="text-[10px] font-mono text-zinc-500 mt-1">{r.sepa.iban_masked}</div>
                            <div className="text-[10px] text-zinc-400">{r.sepa.mandate_reference}</div>
                            {r.sepa.first_collection_done
                              ? <div className="text-[10px] text-emerald-600 mt-0.5">RCUR (Folgeeinzug)</div>
                              : <div className="text-[10px] text-amber-600 mt-0.5">FRST (Erstlastschrift)</div>}
                          </div>
                        ) : (
                          <span className="text-xs text-rose-500">Kein Mandat</span>
                        )}
                        <div className="mt-1">
                          {(r.stripe?.ready || pushedIds.has(r.owner_id)) ? (
                            <Badge
                              className="bg-teal-50 text-teal-700 border border-teal-200 text-[10px]"
                              title="Das SEPA-Mandat liegt bereits als Zahlungsmethode in Stripe — eine erneute Übertragung ist nicht nötig und wird verhindert."
                              data-testid={`stripe-mandate-${r.owner_id}`}
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" />Bereits an Stripe übertragen
                            </Badge>
                          ) : r.sepa?.active ? (
                            <button
                              type="button"
                              onClick={() => pushMandate(r)}
                              disabled={busyId === `push-${r.owner_id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 border border-teal-300 bg-teal-50/60 rounded px-1.5 py-0.5 hover:bg-teal-100 transition-colors disabled:opacity-50 disabled:cursor-wait"
                              title="App-SEPA-Mandat (IBAN + Unterschrift) automatisch als Zahlungsmethode in Stripe anlegen — danach ist Stripe-Einzug möglich"
                              data-testid={`push-mandate-btn-${r.owner_id}`}
                            >
                              {busyId === `push-${r.owner_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                              → Stripe übertragen
                            </button>
                          ) : (
                            <span className="text-[10px] text-zinc-400" title="User hat kein SEPA-Mandat über Stripe eingerichtet — Einzug nur per SEPA-XML möglich">Stripe: kein Mandat</span>
                          )}
                        </div>
                        <div className="mt-1.5">
                          {(r.billing_mode === "stripe_auto" || r.stripe?.subscription) ? (
                            <button
                              type="button"
                              onClick={() => toggleBillingMode(r)}
                              disabled={busyId === `mode-${r.owner_id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 border border-violet-300 bg-violet-50/60 rounded px-1.5 py-0.5 hover:bg-violet-100 transition-colors disabled:opacity-50"
                              title="Stripe-Voll-Automatik: Stripe erstellt & bucht die Monatsrechnungen selbst ab. Klicken, um auf Software-Einzug (Standard) umzustellen."
                              data-testid={`billing-mode-btn-${r.owner_id}`}
                            >
                              {busyId === `mode-${r.owner_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}
                              Stripe-Automatik
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleBillingMode(r)}
                              disabled={busyId === `mode-${r.owner_id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50/60 rounded px-1.5 py-0.5 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                              title={'Software-Einzug (Standard): Die Software erstellt die Rechnung, „Senden" zieht sofort per Stripe-SEPA ein. Klicken, um auf Stripe-Voll-Automatik umzustellen.'}
                              data-testid={`billing-mode-btn-${r.owner_id}`}
                            >
                              {busyId === `mode-${r.owner_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                              Software-Einzug
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right align-top">
                        <ActionsCell row={r} busyId={busyId} handlers={handlersFor(r)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile/Tablet: Card-Liste */}
        <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3" data-testid="billing-cards-mobile">
          {loading ? (
            <div className="md:col-span-2 lg:col-span-1 rounded-2xl border border-zinc-200 bg-white py-10 text-center text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin inline" /> Lade…
            </div>
          ) : rows.length === 0 ? (
            <div className="md:col-span-2 lg:col-span-1 rounded-2xl border border-zinc-200 bg-white py-10 text-center text-zinc-400">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
              <div className="font-medium">Keine Daten</div>
            </div>
          ) : rows.map((r) => {
            const inv = r.invoice;
            const blocked = r.is_blocked;
            return (
              <div
                key={r.owner_id}
                className={`rounded-2xl border p-4 ${blocked ? "bg-rose-50/40 border-rose-200" : "bg-white border-zinc-200"}`}
                data-testid={`billing-card-${r.owner_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-zinc-900 truncate">{r.owner_name}</div>
                      {blocked && <Badge className="bg-rose-100 text-rose-800 text-[10px]"><Ban className="h-3 w-3 mr-1" />Gesperrt</Badge>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{r.owner_email}</div>
                    {inv?.invoice_number && (
                      <div className="text-[11px] font-mono text-sky-700 mt-0.5">{inv.invoice_number}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-zinc-900">
                      {inv ? fmtEur(inv.total_gross) : fmtEur(r.gross_estimate)}
                    </div>
                    {!inv && <div className="text-[10px] text-zinc-400">(geschätzt)</div>}
                    {inv?.due_date && <div className="text-[10px] text-zinc-500 mt-0.5">fällig {fmtDate(inv.due_date)}</div>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Tarif</div>
                    <div className="font-medium mt-0.5">{r.plan_name}</div>
                    <div className="text-[10px] text-zinc-500">{fmtEur(r.base_fee)} + {fmtEur(r.per_case_fee)}/Akte</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Akten</div>
                    <div className="font-mono font-semibold mt-0.5">{r.effective_cases}</div>
                    {r.cases_taken_over > 0 && (
                      <div className="text-[10px] text-amber-700">−{r.cases_taken_over} übernommen</div>
                    )}
                    {r.cases_deducted > 0 && (
                      <div className="text-[10px] text-rose-600">−{r.cases_deducted} abgezogen</div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">SEPA</div>
                    {r.sepa?.active ? (
                      <div className="mt-0.5">
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">
                          <ShieldCheck className="h-3 w-3 mr-1" />Aktiv
                        </Badge>
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{r.sepa.iban_masked}</div>
                      </div>
                    ) : (
                      <div className="text-rose-500 mt-0.5">Kein Mandat</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  {inv ? <StatusPill status={inv.status} /> : <span className="text-xs text-zinc-400">Keine Rechnung</span>}
                  <CollectionBadge inv={inv} onSync={inv ? () => syncCollectOne(inv.id) : undefined} syncing={inv ? busyId === `csync-${inv.id}` : false} />
                  {(r.billing_mode === "stripe_auto" || r.stripe?.subscription) && (
                    <Badge className="bg-violet-50 text-violet-700 border border-violet-200 text-[10px]" data-testid={`billing-mode-badge-m-${r.owner_id}`}>
                      <CreditCard className="h-3 w-3 mr-1" />Stripe-Automatik
                    </Badge>
                  )}
                  {r.stripe?.ready ? (
                    <Badge className="bg-teal-50 text-teal-700 border border-teal-200 text-[10px]">
                      <Zap className="h-3 w-3 mr-1" />Stripe
                    </Badge>
                  ) : r.sepa?.active ? (
                    <button
                      type="button"
                      onClick={() => pushMandate(r)}
                      disabled={busyId === `push-${r.owner_id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 border border-teal-300 bg-teal-50/60 rounded px-1.5 py-0.5 hover:bg-teal-100 transition-colors disabled:opacity-50"
                      data-testid={`push-mandate-btn-m-${r.owner_id}`}
                    >
                      {busyId === `push-${r.owner_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                      → Stripe übertragen
                    </button>
                  ) : null}
                  {inv?.reminder_level > 0 && (
                    <span className="text-[10px] text-amber-700 inline-flex items-center gap-1">
                      <BellRing className="h-3 w-3" />
                      {inv.reminder_level === 1 ? "Erinnert" : `Mahnung ${inv.reminder_level - 1}`}
                    </span>
                  )}
                  {inv?.admin_note && (
                    <span className="text-[10px] text-violet-700 inline-flex items-center gap-1 max-w-full" title={inv.admin_note}>
                      <StickyNote className="h-3 w-3 shrink-0" />
                      <span className="truncate">{inv.admin_note}</span>
                    </span>
                  )}
                </div>
                <div className="mt-3 border-t border-zinc-100 pt-2.5">
                  <ActionsCell row={r} busyId={busyId} handlers={handlersFor(r)} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-xs text-zinc-500 text-center">
          ⏰ Entwürfe werden <b>täglich automatisch</b> erstellt &amp; aktualisiert · „Senden" verschickt
          die Rechnung <b>und zieht sofort per Stripe-SEPA ein</b> · Einzugs-Status (läuft / eingezogen /
          fehlgeschlagen) aktualisiert sich automatisch per Stripe-Webhook
        </div>
      </div>

      {/* Block-Dialog */}
      <BlockDialog open={!!blockDlg} owner={blockDlg?.owner} onClose={() => setBlockDlg(null)} onDone={load} />
      {/* Takeover-Dialog */}
      <CaseTakeoverDialog open={!!deductDlg} owner={deductDlg?.owner} year={year} month={month} onClose={() => setDeductDlg(null)} onDone={load} />
      {/* Reminder-Dialog */}
      <ReminderDialog open={!!reminderDlg} row={reminderDlg?.row} onClose={() => setReminderDlg(null)} onDone={load} />
      {/* Cancel-Dialog */}
      <CancelInvoiceDialog open={!!cancelDlg} row={cancelDlg?.row} onClose={() => setCancelDlg(null)} onDone={load} />
      {/* Note-Dialog */}
      <NoteDialog open={!!noteDlg} row={noteDlg?.row} onClose={() => setNoteDlg(null)} onDone={load} />
      {/* SEPA-XML-Dialog */}
      <SepaXmlDialog open={sepaDlg} year={year} month={month} rows={data?.rows || []} onClose={() => setSepaDlg(false)} onDone={load} />
      {/* Creditor-Settings */}
      <CreditorDialog open={creditorDlg} onClose={() => setCreditorDlg(false)} />
      {/* Stripe-Einzug: Settings + Sammellauf */}
      <StripeCollectDialog open={stripeDlg} year={year} month={month} kpis={k} onClose={() => setStripeDlg(false)} onDone={load} />
      {/* Rückerstattung */}
      <RefundDialog open={!!refundDlg} row={refundDlg?.row} onClose={() => setRefundDlg(null)} onDone={load} />
    </>
  );
}

/* ──────────────────── ActionsCell ──────────────────── */
function ActionsCell({ row, busyId, handlers }) {
  const visible = ACTION_DEFINITIONS.filter((a) => a.shouldShow(row));
  return (
    <div className="flex items-center justify-end gap-1.5 flex-wrap" data-testid={`actions-${row.owner_id}`}>
      {visible.map((a) => {
        const busyKey = a.key === "generate"
          ? `gen-${row.owner_id}`
          : a.key === "unblock"
            ? `unblock-${row.owner_id}`
            : a.key === "pdf" && row.invoice
              ? `pdf-${row.invoice.id}`
              : a.key === "send" && row.invoice
                ? `send-${row.invoice.id}`
                : a.key === "paid" && row.invoice
                  ? `paid-${row.invoice.id}`
                  : null;
        const isBusy = busyKey && busyId === busyKey;
        const Icon = a.icon;
        return (
          <button
            key={a.key}
            type="button"
            onClick={handlers[a.key]}
            disabled={isBusy}
            title={a.description}
            data-testid={`action-${a.key}-${row.owner_id}`}
            className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border bg-white text-[11px] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${COLOR_BTN[a.color] || COLOR_BTN.zinc}`}
          >
            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────── ActionsLegend ──────────────────── */
function ActionsLegend({ onClose }) {
  return (
    <div className="rounded-2xl border-2 border-sky-200 bg-gradient-to-br from-sky-50/40 via-violet-50/20 to-white p-5" data-testid="actions-legend">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Info className="h-5 w-5 text-sky-700" />
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight text-zinc-900">Aktionen — was kann ich pro Zeile tun?</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Diese Buttons erscheinen in der Aktionen-Spalte. Welche sichtbar sind, hängt vom Status der Rechnung & des Users ab.</p>
          </div>
        </div>
        <button onClick={onClose} className="h-7 w-7 rounded-full hover:bg-zinc-100 flex items-center justify-center" data-testid="legend-close">
          <XCircle className="h-4 w-4 text-zinc-500" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ACTION_DEFINITIONS.map((a) => {
          const Icon = a.icon;
          return (
            <div key={a.key} className={`rounded-xl border bg-white p-3 ${COLOR_BTN[a.color] || COLOR_BTN.zinc}`} data-testid={`legend-${a.key}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className="h-4 w-4" />
                <div className="font-bold text-sm">{a.label}</div>
              </div>
              <p className="text-[12px] text-zinc-600 leading-relaxed">{a.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ──────────────────── BlockDialog ──────────────────── */
function BlockDialog({ open, owner, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Bitte einen Grund angeben (mind. 3 Zeichen)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/billing/${owner.owner_id}/block`, { reason: reason.trim() });
      toast.success(`${owner.owner_name} gesperrt`);
      await onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Sperren fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Ban className="h-5 w-5" />User sperren
          </DialogTitle>
          <DialogDescription>
            <b>{owner?.owner_name}</b> ({owner?.owner_email}) wird gesperrt und kann sich nicht mehr einloggen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Grund der Sperre</Label>
          <Textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="z.B. Mehrfach gescheiterte Lastschriftrückläufer, offene Rechnung 60+ Tage"
            rows={3}
            data-testid="block-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="block-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="block-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
            Sperren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── CaseTakeoverDialog ──────────────────── */
function CaseTakeoverDialog({ open, owner, year, month, onClose, onDone }) {
  const [eligible, setEligible] = useState(null);  // { suggested, cases, count }
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [showFullList, setShowFullList] = useState(false);
  const [reason, setReason] = useState("");
  const [createInsuranceInvoice, setCreateInsuranceInvoice] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !owner?.owner_id) return;
    setLoading(true);
    setSelectedCaseId(null);
    setShowFullList(false);
    setReason("");
    setCreateInsuranceInvoice(true);
    api.get(`/admin/billing/${owner.owner_id}/eligible-cases?year=${year}&month=${month}&limit=50`)
      .then(({ data }) => {
        setEligible(data);
        if (data.suggested) setSelectedCaseId(data.suggested.id);
      })
      .catch((e) => toast.error(apiError(e, "Akten konnten nicht geladen werden")))
      .finally(() => setLoading(false));
  }, [open, owner?.owner_id, year, month]);

  const selectedCase = useMemo(() => {
    if (!eligible || !selectedCaseId) return null;
    return eligible.cases.find((c) => c.id === selectedCaseId)
        || (eligible.suggested?.id === selectedCaseId ? eligible.suggested : null);
  }, [eligible, selectedCaseId]);

  const openInvoice = owner?.invoice && owner.invoice.status !== "paid" && owner.invoice.status !== "cancelled"
    ? owner.invoice : null;

  const submit = async () => {
    if (!selectedCaseId) {
      toast.error("Bitte eine Akte wählen");
      return;
    }
    if (reason.trim().length < 3) {
      toast.error("Bitte einen Grund angeben (mind. 3 Zeichen)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/billing/takeover-case", {
        case_id: selectedCaseId,
        reason: reason.trim(),
        subscription_invoice_id: openInvoice?.id || undefined,
        create_insurance_invoice: createInsuranceInvoice,
      });
      toast.success(`Akte übernommen · Honorar ${fmtEur(data.honorar_gross)}${data.credit_id ? " als Gutschrift gebucht" : ""}`);
      await onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Übernahme fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  const total = selectedCase?.honorar_gross || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[680px]" data-testid="takeover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <MinusCircle className="h-5 w-5" />Akte übernehmen
          </DialogTitle>
          <DialogDescription>
            <b>{owner?.owner_name}</b> hat eine offene Abo-Rechnung. EasyGutachter übernimmt
            eine seiner Akten als Kompensation, rechnet sie direkt mit der Versicherung ab
            und schreibt das Honorar gut.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-zinc-400">
            <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Lade Akten…
          </div>
        ) : !eligible?.count ? (
          <div className="py-10 text-center text-zinc-500" data-testid="takeover-empty">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
            <div className="font-semibold">Keine verfügbaren Akten</div>
            <p className="text-xs mt-1">Der Gutachter hat keine Akten mit Honorar &gt; 0 €,
              oder alle wurden bereits übernommen.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Top-Vorschlag */}
            {eligible.suggested && !showFullList && (
              <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4" data-testid="takeover-suggestion">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <span className="text-lg">💡</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Vorschlag (teuerste Akte)</div>
                    <div className="font-bold text-zinc-900 mt-0.5">{eligible.suggested.case_number} · {eligible.suggested.title}</div>
                    <div className="text-xs text-zinc-600 mt-0.5">
                      {eligible.suggested.customer_name && <span>Kunde: <b>{eligible.suggested.customer_name}</b> · </span>}
                      {eligible.suggested.insurance_name && <span>VS: <b>{eligible.suggested.insurance_name}</b> · </span>}
                      {eligible.suggested.license_plate && <span>{eligible.suggested.license_plate}</span>}
                    </div>
                    <div className="text-2xl font-bold text-amber-700 mt-1.5">{fmtEur(eligible.suggested.honorar_gross)}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFullList(true)}
                  className="mt-3 text-xs text-sky-700 hover:underline"
                  data-testid="takeover-show-all"
                >
                  → Andere Akte wählen ({eligible.count - 1} weitere)
                </button>
              </div>
            )}

            {/* Vollständige Akten-Liste */}
            {showFullList && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Akte wählen ({eligible.count})</Label>
                  {eligible.suggested && (
                    <button
                      type="button"
                      onClick={() => { setShowFullList(false); setSelectedCaseId(eligible.suggested.id); }}
                      className="text-xs text-sky-700 hover:underline"
                      data-testid="takeover-back-to-suggestion"
                    >
                      ← Zurück zum Vorschlag
                    </button>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto rounded-xl border border-zinc-200 divide-y divide-zinc-100" data-testid="takeover-case-list">
                  {eligible.cases.map((c) => {
                    const sel = c.id === selectedCaseId;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedCaseId(c.id)}
                        className={`w-full text-left p-3 transition ${sel ? "bg-amber-50" : "hover:bg-zinc-50"}`}
                        data-testid={`takeover-case-${c.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-zinc-900 truncate">{c.case_number} · {c.title}</div>
                            <div className="text-[11px] text-zinc-500 truncate">
                              {c.customer_name || "—"} {c.license_plate && `· ${c.license_plate}`} {c.insurance_name && `· VS: ${c.insurance_name}`}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono font-bold text-amber-700">{fmtEur(c.honorar_gross)}</div>
                            <div className="text-[10px] text-zinc-400">{c.status}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Auswirkungs-Vorschau */}
            {selectedCase && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-3 space-y-2" data-testid="takeover-impact">
                <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700">Was passiert</div>
                <div className="space-y-1.5 text-xs text-zinc-700">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">1.</span>
                    <div>Akte <b>{selectedCase.case_number}</b> wird als <b>„übernommen&ldquo;</b> markiert · für den Gutachter ausgeblendet.</div>
                  </div>
                  {createInsuranceInvoice && selectedCase.insurance_name && (
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">2.</span>
                      <div>Neue Rechnung über <b>{fmtEur(total)}</b> wird an <b>{selectedCase.insurance_name}</b> erstellt (im Status „Entwurf&ldquo;).</div>
                    </div>
                  )}
                  {openInvoice && (
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">{createInsuranceInvoice && selectedCase.insurance_name ? "3." : "2."}</span>
                      <div>Gutschrift über <b>{fmtEur(total)}</b> auf die offene Abo-Rechnung <b>{openInvoice.invoice_number}</b>.</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedCase && selectedCase.insurance_name && (
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={createInsuranceInvoice}
                  onChange={(e) => setCreateInsuranceInvoice(e.target.checked)}
                  className="mt-0.5"
                  data-testid="takeover-create-invoice"
                />
                <span>Rechnung an Versicherung erzeugen (Entwurf — kann nachträglich versendet werden)</span>
              </label>
            )}

            <div>
              <Label>Grund der Übernahme</Label>
              <Textarea
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="z.B. Abo-Rechnung Februar offen · 2 Mahnungen erfolglos · Kompensation"
                rows={2}
                data-testid="takeover-reason"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="takeover-cancel">Abbrechen</Button>
          <Button
            onClick={submit}
            disabled={busy || !selectedCaseId || !eligible?.count}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="takeover-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MinusCircle className="h-4 w-4 mr-2" />}
            Akte übernehmen{selectedCase && ` (${fmtEur(total)})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── ReminderDialog ──────────────────── */
function ReminderDialog({ open, row, onClose, onDone }) {
  const inv = row?.invoice;
  const currentLevel = Number(inv?.reminder_level || 0);
  const [level, setLevel] = useState(Math.min(3, currentLevel + 1));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setLevel(Math.min(3, currentLevel + 1));
      setNote("");
    }
  }, [open, currentLevel]);

  const LEVELS = {
    1: { name: "Zahlungserinnerung", desc: "Freundliche Erinnerung: Rechnung noch offen, bitte überweisen.", color: "bg-sky-50 text-sky-800 border-sky-200" },
    2: { name: "1. Mahnung",          desc: "Erste Mahnstufe: Keine Zahlung trotz Erinnerung. Stärkerer Ton.", color: "bg-amber-50 text-amber-800 border-amber-200" },
    3: { name: "Letzte Mahnung",      desc: "Finale Stufe: Drohung mit Sperrung des Zugangs.",                    color: "bg-rose-50 text-rose-800 border-rose-200" },
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/subscription-invoices/${inv.id}/reminder`,
        { level, note },
      );
      toast.success(`${data.label} verschickt (${data.mail?.mode === "resend" ? "Email gesendet" : "DRY-RUN — kein RESEND_API_KEY"})`);
      await onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Erinnerung fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <BellRing className="h-5 w-5" />Zahlungserinnerung senden
          </DialogTitle>
          <DialogDescription>
            Rechnung <b>{inv?.invoice_number}</b> über <b>{fmtEur(inv?.total_gross)}</b> an <b>{row?.owner_name}</b>.
            {currentLevel > 0 && (
              <span className="block mt-1 text-amber-700 text-xs">Bereits {currentLevel}× erinnert · zuletzt {fmtDate(inv?.last_reminder_at)}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Mahnstufe wählen</Label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {[1, 2, 3].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLevel(l)}
                  data-testid={`reminder-level-${l}`}
                  className={`text-left rounded-xl border-2 p-3 transition ${
                    level === l ? `${LEVELS[l].color} shadow-sm` : "bg-white border-zinc-200 hover:border-amber-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${level === l ? "bg-white" : "bg-zinc-100"}`}>{l}</div>
                    <div className="font-bold text-sm">{LEVELS[l].name}</div>
                  </div>
                  <p className="text-[12px] text-zinc-600 mt-1 ml-9">{LEVELS[l].desc}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Zusätzliche Notiz (optional, erscheint in der Mail)</Label>
            <Textarea
              rows={2}
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Bitte bis 28. Februar überweisen, sonst leiten wir Sperrung ein."
              data-testid="reminder-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="reminder-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="reminder-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BellRing className="h-4 w-4 mr-2" />}
            Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── CancelInvoiceDialog ──────────────────── */
function CancelInvoiceDialog({ open, row, onClose, onDone }) {
  const inv = row?.invoice;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Bitte einen Stornogrund angeben (mind. 3 Zeichen).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/subscription-invoices/${inv.id}/cancel`, { reason: reason.trim() });
      toast.success(`Rechnung ${inv.invoice_number} storniert`);
      await onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Stornierung fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="cancel-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <XCircle className="h-5 w-5" />Rechnung stornieren
          </DialogTitle>
          <DialogDescription>
            Rechnung <b>{inv?.invoice_number}</b> über <b>{fmtEur(inv?.total_gross)}</b> wird storniert.
            <span className="block mt-1 text-rose-700 text-xs">Bezahlte Rechnungen können nicht storniert werden.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Stornogrund</Label>
          <Textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="z.B. Falscher Tarif berechnet, Doppelte Erfassung, Kulanz"
            rows={3}
            data-testid="cancel-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="cancel-back">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="cancel-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
            Stornieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── NoteDialog ──────────────────── */
function NoteDialog({ open, row, onClose, onDone }) {
  const inv = row?.invoice;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setNote(inv?.admin_note || ""); }, [open, inv?.admin_note]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/subscription-invoices/${inv.id}/admin-note`, { note: note.trim() });
      toast.success(note.trim() ? "Notiz gespeichert" : "Notiz entfernt");
      await onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-700">
            <StickyNote className="h-5 w-5" />Interne Admin-Notiz
          </DialogTitle>
          <DialogDescription>
            Rechnung <b>{inv?.invoice_number}</b> von <b>{row?.owner_name}</b>.
            <span className="block mt-1 text-zinc-500 text-xs">Diese Notiz ist NICHT für den Gutachter sichtbar — nur intern im Cockpit.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Notiz {inv?.admin_note_updated_at && <span className="text-[10px] text-zinc-400 font-normal">· zuletzt {fmtDate(inv.admin_note_updated_at)} von {inv.admin_note_updated_by}</span>}</Label>
          <Textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="z.B. Telefonat 12.02: Kunde zahlt am 28.02 manuell · Lastschrift-Rückläufer wegen Kontoumstellung"
            rows={5}
            data-testid="note-textarea"
          />
          <p className="text-[11px] text-zinc-500">Leeren + Speichern entfernt die Notiz.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="note-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="note-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <StickyNote className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── SepaXmlDialog ──────────────────── */
function SepaXmlDialog({ open, year, month, rows, onClose, onDone }) {
  // Standard: heute + 3 Werktage
  const defaultColl = useMemo(() => {
    const d = new Date();
    let added = 0;
    while (added < 3) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d.toISOString().slice(0, 10);
  }, []);

  const [collectionDate, setCollectionDate] = useState(defaultColl);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setCollectionDate(defaultColl); }, [open, defaultColl]);

  // Vorschau: welche Rechnungen werden eingezogen?
  const eligible = useMemo(() =>
    rows.filter((r) => r.invoice && r.sepa?.active && r.invoice.status !== "paid" && (r.invoice.total_gross || 0) > 0),
    [rows]
  );
  const total = eligible.reduce((s, r) => s + (r.invoice?.total_gross || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        "/admin/billing/sepa-xml",
        { year, month, collection_date: collectionDate },
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEPA-Lastschrift-${year}-${String(month).padStart(2, "0")}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const txCount = r.headers["x-sepa-transactions"] || "?";
      const skipped = r.headers["x-sepa-skipped"] || "0";
      toast.success(`SEPA-XML erzeugt · ${txCount} Buchungen${skipped !== "0" ? ` · ${skipped} übersprungen` : ""}`);
      await onDone();
      onClose();
    } catch (e) {
      let msg = "SEPA-XML fehlgeschlagen";
      try {
        if (e.response?.data?.text) msg = await e.response.data.text();
        else if (e.response?.data) msg = JSON.parse(await e.response.data.text()).detail || msg;
      } catch { /* ignore */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px]" data-testid="sepa-xml-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sky-700">
            <Download className="h-5 w-5" />SEPA-XML herunterladen
          </DialogTitle>
          <DialogDescription>
            Erzeugt eine <b>pain.008.001.02</b> XML-Datei mit allen offenen Rechnungen für
            <b> {MONTHS[month - 1]} {year}</b>, die ein aktives SEPA-Mandat haben.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700 mb-1">Vorschau</div>
            <div className="text-2xl font-bold text-zinc-900">{fmtEur(total)}</div>
            <div className="text-xs text-zinc-600 mt-0.5">
              {eligible.length} Lastschrift{eligible.length === 1 ? "" : "en"} · {eligible.filter((e) => !e.sepa.first_collection_done).length} FRST · {eligible.filter((e) => e.sepa.first_collection_done).length} RCUR
            </div>
          </div>

          <div>
            <Label>Einzugsdatum (Fälligkeit)</Label>
            <Input
              type="date"
              value={collectionDate}
              onChange={(e) => setCollectionDate(e.target.value)}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              data-testid="sepa-collection-date"
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              FRST = mind. 5 Bankarbeitstage in der Zukunft · RCUR = mind. 2 Bankarbeitstage
            </p>
          </div>

          {eligible.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-xl border border-zinc-200">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-2 py-1.5">Gutachter</th>
                    <th className="text-left px-2 py-1.5">IBAN</th>
                    <th className="text-left px-2 py-1.5">SeqTp</th>
                    <th className="text-right px-2 py-1.5">Betrag</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {eligible.map((r) => (
                    <tr key={r.owner_id}>
                      <td className="px-2 py-1.5 text-zinc-900 font-medium">{r.owner_name}</td>
                      <td className="px-2 py-1.5 font-mono text-zinc-500">{r.sepa.iban_masked}</td>
                      <td className="px-2 py-1.5">
                        <Badge className={`text-[9px] ${r.sepa.first_collection_done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.sepa.first_collection_done ? "RCUR" : "FRST"}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold">{fmtEur(r.invoice.total_gross)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="sepa-cancel">Abbrechen</Button>
          <Button
            onClick={submit}
            disabled={busy || eligible.length === 0}
            className="bg-gradient-to-r from-sky-500 to-violet-600 text-white"
            data-testid="sepa-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            XML herunterladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── CreditorDialog ──────────────────── */
function CreditorDialog({ open, onClose }) {
  const [data, setData] = useState({ name: "", iban: "", bic: "", creditor_id: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/billing/creditor")
      .then(({ data }) => setData(data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/billing/creditor", {
        name: data.creditor_name || data.name,
        iban: data.creditor_iban || data.iban,
        bic: data.creditor_bic || data.bic,
        creditor_id: data.creditor_id,
        address: data.address,
      });
      toast.success("Gläubiger-Stammdaten gespeichert");
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  const f = (key) => (e) => setData((p) => ({ ...p, [key]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="creditor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sky-700">
            <Building2 className="h-5 w-5" />Gläubiger-Stammdaten
          </DialogTitle>
          <DialogDescription>
            Wird in jeder erzeugten SEPA-XML als Zahlungsempfänger eingetragen.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Firmenname</Label>
              <Input value={data.creditor_name || data.name || ""} onChange={f("creditor_name")} data-testid="creditor-name" />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input value={data.address || ""} onChange={f("address")} data-testid="creditor-address" />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input value={data.creditor_iban || data.iban || ""} onChange={f("creditor_iban")} className="font-mono" data-testid="creditor-iban" />
            </div>
            <div>
              <Label>BIC (optional)</Label>
              <Input value={data.creditor_bic || data.bic || ""} onChange={f("creditor_bic")} className="font-mono" placeholder="leer = NOTPROVIDED" data-testid="creditor-bic" />
            </div>
            <div>
              <Label>Gläubiger-ID (Creditor-ID)</Label>
              <Input value={data.creditor_id || ""} onChange={f("creditor_id")} className="font-mono" placeholder="DE..." data-testid="creditor-id" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="creditor-save">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── StripeCollectDialog ────────────────────
   Zentrale Steuerung des Stripe-Einzugs:
   • Auto-Einzug an/aus + Tage nach Versand
   • Sammellauf „Alle fälligen jetzt einziehen"
   • Mandate-Sync (cached SEPA-PaymentMethods aller Stripe-Kunden)      */
function StripeCollectDialog({ open, year, month, kpis, onClose, onDone }) {
  const [settings, setSettings] = useState({ auto_collect_enabled: false, days_after_send: 3, auto_backfill_enabled: true });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushRun, setPushRun] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLastRun(null);
    setLoadingSettings(true);
    api.get("/admin/subscription-invoices/collect/settings")
      .then(({ data }) => setSettings(data))
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, [open]);

  const saveSettings = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/subscription-invoices/collect/settings", next);
      setSettings(data);
      toast.success(data.auto_collect_enabled
        ? `Auto-Einzug aktiv — ${data.days_after_send} Tage nach Rechnungsversand`
        : "Auto-Einzug deaktiviert");
    } catch (e) {
      toast.error(apiError(e, "Einstellungen konnten nicht gespeichert werden"));
    } finally { setSaving(false); }
  };

  const collectAll = async () => {
    if (!window.confirm(
      `Alle versendeten, offenen Rechnungen für ${MONTHS[month - 1]} ${year} jetzt per Stripe-SEPA einziehen?\n\n`
      + "Nur Kunden mit Stripe-SEPA-Mandat werden eingezogen — alle anderen werden übersprungen (Liste folgt)."
    )) return;
    setCollecting(true);
    try {
      const { data } = await api.post("/admin/subscription-invoices/collect-due", { year, month });
      setLastRun(data);
      toast.success(`${data.collected_count} Einzüge gestartet${data.skipped_count ? ` · ${data.skipped_count} übersprungen` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Sammellauf fehlgeschlagen"));
    } finally { setCollecting(false); }
  };

  const syncMandates = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/subscription-invoices/collect/sync-mandates");
      toast.success(`Mandate synchronisiert: ${data.with_mandate} mit Mandat · ${data.without_mandate} ohne${data.errors ? ` · ${data.errors} Fehler` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Sync fehlgeschlagen"));
    } finally { setSyncing(false); }
  };

  const pushAllMandates = async () => {
    if (!window.confirm(
      "Alle App-SEPA-Mandate jetzt zu Stripe übertragen?\n\n"
      + "Für jeden Kunden mit aktivem App-Mandat ohne Stripe-Zahlungsmethode wird die "
      + "IBAN als SEPA-Zahlungsmethode in Stripe angelegt. Es wird KEIN Geld abgebucht."
    )) return;
    setPushing(true);
    setPushRun(null);
    try {
      const { data } = await api.post("/admin/subscription-invoices/collect/push-mandates");
      setPushRun(data);
      toast.success(`${data.pushed_count} Mandat${data.pushed_count === 1 ? "" : "e"} übertragen${data.skipped_count ? ` · ${data.skipped_count} übersprungen` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Übertragung fehlgeschlagen"));
    } finally { setPushing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[600px]" data-testid="stripe-collect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-600" /> Stripe-Einzug steuern
          </DialogTitle>
          <DialogDescription>
            Deine Rechnung bleibt führend — Stripe zieht die Beträge per SEPA-Lastschrift ein.
            Der Status kommt automatisch zurück (bezahlt / fehlgeschlagen mit Grund).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Voll-Automatik: Abo-Anlage + Adress-Sync */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4" data-testid="auto-backfill-settings">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-zinc-900">Stripe Voll-Automatik (Abos)</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Legt fehlende Stripe-Abos aktiver Abonnenten automatisch an (inkl. SEPA-Mandat-Übertragung
                  und Kundenadresse) — Stripe erstellt & bucht die Monatsrechnungen dann von selbst ab.
                  Läuft stündlich, kein manueller Schritt nötig.
                </div>
              </div>
              <Switch
                checked={settings.auto_backfill_enabled}
                disabled={loadingSettings || saving}
                onCheckedChange={(v) => saveSettings({ ...settings, auto_backfill_enabled: v })}
                data-testid="auto-backfill-switch"
              />
            </div>
          </div>

          {/* Auto-Einzug */}
          <div className="rounded-xl border border-zinc-200 p-4" data-testid="auto-collect-settings">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-zinc-900">Automatischer Einzug</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Zieht jede Rechnung automatisch X Tage nach Versand ein (nur Erstversuch —
                  fehlgeschlagene Einzüge wiederholst du manuell).
                </div>
              </div>
              <Switch
                checked={settings.auto_collect_enabled}
                disabled={loadingSettings || saving}
                onCheckedChange={(v) => saveSettings({ ...settings, auto_collect_enabled: v })}
                data-testid="auto-collect-switch"
              />
            </div>
            {settings.auto_collect_enabled && (
              <div className="flex items-center gap-2 mt-3">
                <Label className="text-xs text-zinc-600">Einzug</Label>
                <Input
                  type="number" min={0} max={30}
                  value={settings.days_after_send}
                  onChange={(e) => setSettings((s) => ({ ...s, days_after_send: Number(e.target.value) }))}
                  onBlur={() => saveSettings(settings)}
                  className="h-8 w-20 text-sm"
                  data-testid="auto-collect-days"
                />
                <span className="text-xs text-zinc-600">Tage nach Rechnungsversand</span>
              </div>
            )}
          </div>

          {/* Sammellauf */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <div className="text-sm font-semibold text-zinc-900">
              Sammellauf · {MONTHS[month - 1]} {year}
            </div>
            <div className="text-xs text-zinc-600 mt-0.5 mb-3">
              {kpis?.sent_count ?? 0} versendete Rechnungen · {kpis?.stripe_ready ?? 0} Kunden mit Stripe-Mandat
            </div>
            <Button
              onClick={collectAll}
              disabled={collecting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="collect-all-btn"
            >
              {collecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Alle fälligen jetzt einziehen
            </Button>
            {lastRun && (
              <div className="mt-3 text-xs space-y-1" data-testid="collect-run-result">
                <div className="font-semibold text-emerald-700">✓ {lastRun.collected_count} Einzüge gestartet</div>
                {(lastRun.skipped || []).slice(0, 6).map((s, i) => (
                  <div key={i} className="text-zinc-600">• {s.invoice_number || "—"}: {s.reason}</div>
                ))}
                {(lastRun.skipped || []).length > 6 && (
                  <div className="text-zinc-400">… und {lastRun.skipped.length - 6} weitere</div>
                )}
              </div>
            )}
          </div>

          {/* App-Mandate → Stripe übertragen */}
          <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4" data-testid="push-mandates-section">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-zinc-900">App-Mandate zu Stripe übertragen</div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  Legt für alle Kunden mit App-SEPA-Mandat (IBAN + Unterschrift), die noch nicht in
                  Stripe hinterlegt sind, automatisch die Zahlungsmethode + das Mandat in Stripe an.
                  Es wird kein Geld abgebucht.
                </div>
              </div>
              <Button
                onClick={pushAllMandates}
                disabled={pushing}
                className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                size="sm"
                data-testid="push-mandates-btn"
              >
                {pushing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
                Übertragen
              </Button>
            </div>
            {pushRun && (
              <div className="mt-3 text-xs space-y-1" data-testid="push-mandates-result">
                <div className="font-semibold text-teal-700">✓ {pushRun.pushed_count} Mandat{pushRun.pushed_count === 1 ? "" : "e"} übertragen</div>
                {(pushRun.skipped || []).slice(0, 6).map((s, i) => (
                  <div key={i} className="text-zinc-600">• {s.email}: {s.reason}</div>
                ))}
                {(pushRun.skipped || []).length > 6 && (
                  <div className="text-zinc-400">… und {pushRun.skipped.length - 6} weitere</div>
                )}
              </div>
            )}
          </div>

          {/* Mandate-Sync */}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 p-4">
            <div>
              <div className="text-sm font-semibold text-zinc-900">Stripe-Mandate synchronisieren</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Aktualisiert die „Stripe"-Badges: prüft für alle Stripe-Kunden, ob ein SEPA-Mandat hinterlegt ist.
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={syncMandates} disabled={syncing} data-testid="sync-mandates-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1.5" />}
              Sync
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="stripe-collect-close">Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── RefundDialog ──────────────────── */
function RefundDialog({ open, row, onClose, onDone }) {
  const inv = row?.invoice;
  const total = Number(inv?.total_gross || 0);
  const already = Number(inv?.refund_amount || 0);
  const maxAmount = Math.max(0, Math.round((total - already) * 100) / 100);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setAmount(String(maxAmount)); setReason(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inv?.id]);

  const submit = async () => {
    const amt = Number(String(amount).replace(",", "."));
    if (!(amt > 0) || amt > maxAmount + 0.001) {
      toast.error(`Betrag muss zwischen 0,01 € und ${fmtEur(maxAmount)} liegen`);
      return;
    }
    if (!window.confirm(`${fmtEur(amt)} an ${row.owner_name} zurückerstatten?\n\nDie SEPA-Gutschrift dauert einige Banktage.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/subscription-invoices/${inv.id}/refund`, { amount_eur: amt, reason });
      toast.success("Rückerstattung veranlasst");
      onDone?.(); onClose();
    } catch (e) {
      toast.error(apiError(e, "Rückerstattung fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-violet-600" /> Rückerstattung
          </DialogTitle>
          <DialogDescription>
            Rechnung {inv?.invoice_number} · {row?.owner_name} · bezahlt {fmtEur(total)}
            {already > 0 && <> · bereits erstattet {fmtEur(already)}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Betrag (max. {fmtEur(maxAmount)})</Label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              className="h-9 mt-1"
              data-testid="refund-amount"
            />
          </div>
          <div>
            <Label className="text-xs">Grund (intern, optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="z.B. Kulanz, Doppelbelastung…"
              className="mt-1"
              data-testid="refund-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="refund-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="refund-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-2" />}
            Erstatten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

